<script setup>
import { computed, ref } from 'vue';
import { styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const items = computed(() => (Array.isArray(props.config.items) ? props.config.items : [])
    .filter((it) => it && String(it.q ?? '').trim() !== ''));

// One answer open at a time, as in the mockup. Pressing the open one
// closes it, so there is always a way back to the plain list.
const openIndex = ref(-1);
function toggle(i) { openIndex.value = openIndex.value === i ? -1 : i; }
function panelId(i) { return `${props.config.anchor_id || 'faq'}-answer-${i}`; }
</script>

<template>
    <section v-if="items.length"
             :id="config.anchor_id || null"
             class="th-block th-sec th-faq"
             :style="styleVars(config)">
        <div class="th-wrap th-faq__inner">
            <h2 v-if="config.heading" class="th-h2 th-faq__heading">{{ config.heading }}</h2>
            <div class="th-faq__list">
                <div v-for="(item, i) in items" :key="i" class="th-faq__row">
                    <button type="button"
                            class="th-faq__q"
                            :aria-expanded="openIndex === i ? 'true' : 'false'"
                            :aria-controls="panelId(i)"
                            @click="toggle(i)">
                        <span>{{ item.q }}</span>
                        <span class="th-faq__sign" aria-hidden="true">{{ openIndex === i ? '–' : '+' }}</span>
                    </button>
                    <p v-show="openIndex === i" :id="panelId(i)" class="th-faq__a">{{ item.a }}</p>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-faq__inner {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr));
    gap: clamp(24px, 4vw, 56px);
    align-items: start;
}
.th-faq__heading { margin: 0; }
.th-faq__list { display: flex; flex-direction: column; border-top: 1px solid var(--th-rule); }
.th-faq__row { border-bottom: 1px solid var(--th-rule); }
.th-faq__q {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    background: none;
    border: 0;
    padding: 18px 0;
    text-align: left;
    font: 700 18px/1.3 var(--th-display);
    color: inherit;
}
.th-faq__sign { font-size: 22px; color: var(--accent); line-height: 1; }
.th-faq__a {
    margin: 0 0 18px;
    font-size: 16px;
    max-width: 60ch;
    color: var(--text-sub, var(--th-muted));
    text-wrap: pretty;
}
</style>
